import { NoticeBlock } from "@/components/NoticeBlock.tsx";
import T from "@/components/forms/TextField.module.css";

import { type AriaFieldOptions, useAriaField } from "@/components/forms/ariaFieldRegister.ts";
import { cardNumberLabel } from "@/modules/billing/payment_forms/labels.tsx";
import type { Translatable } from "@/modules/l10n/getTransText.ts";
import { Trans, useLingui } from "@lingui/react/macro";
import type { ComponentProps, ReactNode } from "react";
import { Input, Label, Text, TextField } from "react-aria-components";
import type { FieldPath, FieldValues, UseFormReturn } from "react-hook-form";

//#region Layouts

export function DescriptionLayout({
    children,
    label,
    description,
}: {
    children: ReactNode;
    label: ReactNode;
    description: ReactNode;
}) {
    return (
        <>
            <Label className={T.Label}>{label}</Label>
            <div className="flex gap-2">
                <div>{children}</div>
                <Description>{description}</Description>
            </div>
        </>
    );
}

function Description({ children }: { children: ReactNode }) {
    return (
        <Text slot="description" className="text-muted font-size-small">
            {children}
        </Text>
    );
}

//#endregion

//#region Error display

export function ErrorText({ text }: { text: ReactNode | undefined }) {
    return text != null && <div className="text-error">{text}</div>;
}

export function ErrorTexts({ errors }: { errors: ReactNode[] }) {
    if (errors.length === 0) {
        return null;
    }

    return (
        <>
            {errors.map((error, i) => (
                <ErrorText key={i} text={error} />
            ))}
        </>
    );
}

//#endregion

//#region Standard fields

export function CardNameField<T extends FieldValues>(props: Omit<StandardFieldProps<T>, "label" | "options">) {
    const { t } = useLingui();
    const minChars = 4;
    return (
        <StandardField<T>
            label={<Trans>Name (Min {minChars} chars)</Trans>}
            options={{
                isRequired: true,
                setValueAs: (v: string) => v.trim(),
                validate: (v: string) => v.length >= minChars || t`Name is too short`,
            }}
            autoComplete="cc-name"
            {...props}
        />
    );
}

export function CardNumberField<T extends FieldValues>(props: Omit<StandardFieldProps<T>, "label" | "options">) {
    return (
        <StandardField<T>
            {...cardNumberLabel}
            options={{
                isRequired: true,
            }}
            autoComplete="cc-number"
            inputMode="numeric"
            {...props}
        />
    );
}

export interface StandardFieldProps<T extends FieldValues> extends Pick<
    ComponentProps<"input">,
    "type" | "autoComplete" | "inputMode"
> {
    form: UseFormReturn<T>;
    name: FieldPath<T>;
    label: ReactNode;
    options: AriaFieldOptions<T>;
    placeholder?: string;
    autoFocus?: boolean;
}

export function StandardField<T extends FieldValues>({
    form,
    name,
    label,
    options,
    placeholder,
    autoFocus,
    ...inputProps
}: StandardFieldProps<T>) {
    const { props, ref } = useAriaField(name, form, options);

    return (
        <TextField className={T.TextField} autoFocus={autoFocus} {...props}>
            <Label className={T.Label}>{label}</Label>
            <Input ref={ref} className={T.Input} placeholder={placeholder} {...inputProps} />
            <ErrorText text={props.errorMessage} />
        </TextField>
    );
}
export const PaymentResponseTakesTimeNotice = ({ additionalMessage }: { additionalMessage?: Translatable }) => (
    <NoticeBlock color="primary" icon="jp-warning-icon">
        <Trans>
            Payment processing with this method takes some time. Purchased credit will be automatically added to your
            account once the process is complete.
        </Trans>
        {additionalMessage && <> {additionalMessage}</>}
    </NoticeBlock>
);

//#endregion
